import { Suspense, lazy } from 'react';
import { Navigate, Route, Routes, useLocation } from 'react-router-dom';

import { AppShell } from '@/components/AppShell';
import { LoadingBlock } from '@/components/PageHeader';
import { RequireAuth } from '@/components/RequireAuth';
import { LoginPage } from '@/pages/login/LoginPage';
import { SafeDashboardPage } from '@/pages/safe/SafeDashboardPage';
import { DOC_TYPES, type DocType } from '@/pages/safe/docs/types';
import { SafeJournalPage } from '@/pages/safe/SafeJournalPage';
import { CaseLibraryPage } from '@/pages/legacy/CaseLibraryPage';
import { AccountsPage } from '@/pages/accounts/AccountsPage';
import { LegacyArchivePage } from '@/pages/legacy/LegacyArchivePage';
import { SafeSearchPage } from '@/pages/legacy/SafeSearchPage';
import { MySitesPage } from '@/pages/site/MySitesPage';
import { SiteSectionPage } from '@/pages/site/SiteSectionPage';

/** 안전교육 이수확인서 QR 이 여는 화면(DOC-129). 근로자 휴대폰만 여는 자리라 미리 싣지 않는다. */
const SafeSignPage = lazy(() => import('@/pages/sign/SafeSignPage'));
/** 근로자 아차사고 · 의견 접수 · TBM 참여자 서명 QR(DOC-136). 근로자 휴대폰만 여는 자리라 미리 싣지 않는다. */
const VoiceReportPage = lazy(() => import('@/pages/public/VoiceReportPage'));
const TbmSignPage = lazy(() => import('@/pages/public/TbmSignPage'));

/*
  안전 홈페이지(safe.bomienc.com) — 그룹웨어의 「안전 관리」 메뉴를 떼어 낸 자리다.
  계정·API 는 그룹웨어와 같다(`/api/v1` · 권한 키 `safe` 하나). 그룹웨어의 옛 주소(`/safety/*` · `/safety-sign/*`)는
  front 가 이 주소로 넘긴다(front `lib/safeHome.ts`).

  일지 넷은 화면 하나(`SafeJournalPage`)이고 주소가 종류를 고른다. `/sites/:siteId/…` 는 대시보드 [입장] 이 여는 현장 화면이다
  — 좌측 메뉴가 그 현장의 세부 메뉴로 바뀐다(`pages/site/siteMenu.ts`).
*/
/** 옛 `/login?returnTo=…` — 질의를 그대로 들고 관문으로. */
function LoginRedirect() {
  const { search } = useLocation();
  return <Navigate to={`/${search}`} replace />;
}

export function App() {
  return (
    <Routes>
      {/* 첫 화면은 관문이다 — 로그인해 있어도 여기서 문을 눌러 들어간다(vendor 와 같다). 옛 `/login` 은 이리로 온다. */}
      <Route path="/" element={<LoginPage />} />
      <Route path="/login" element={<LoginRedirect />} />
      {/* 근로자는 계정이 없어 문지기 · 껍데기 바깥에 선다. 인가는 주소의 토큰이 한다. */}
      <Route
        path="/sign/:token"
        element={
          <Suspense fallback={<LoadingBlock />}>
            <SafeSignPage />
          </Suspense>
        }
      />
      <Route
        path="/voice/:token"
        element={
          <Suspense fallback={<LoadingBlock />}>
            <VoiceReportPage />
          </Suspense>
        }
      />
      <Route
        path="/tbm-sign/:token"
        element={
          <Suspense fallback={<LoadingBlock />}>
            <TbmSignPage />
          </Suspense>
        }
      />
      <Route
        element={
          <RequireAuth>
            <AppShell />
          </RequireAuth>
        }
      >
        <Route path="/dashboard" element={<SafeDashboardPage />} />
        <Route path="/education" element={<SafeJournalPage key="EDUCATION" type="EDUCATION" />} />
        <Route path="/health" element={<SafeJournalPage key="HEALTH" type="HEALTH" />} />
        <Route path="/patrol" element={<SafeJournalPage key="PATROL" type="PATROL" />} />
        <Route path="/joint" element={<SafeJournalPage key="JOINT" type="JOINT" />} />
        <Route path="/tbm" element={<SafeJournalPage key="TBM" type="TBM" />} />
        <Route path="/risk" element={<SafeJournalPage key="RISK" type="RISK" />} />
        {/* 양식 문서(V574) — 할일 링크(`/education?open=`)가 종류 주소로 넘길 자리. 메뉴는 현장 화면에만 있다. */}
        {(Object.keys(DOC_TYPES) as DocType[]).map((code) => (
          <Route key={code} path={DOC_TYPES[code].path} element={<SafeJournalPage key={code} type={code} />} />
        ))}
        <Route path="/my-sites" element={<MySitesPage />} />
        <Route path="/legacy" element={<LegacyArchivePage />} />
        <Route path="/search" element={<SafeSearchPage />} />
        <Route path="/cases" element={<CaseLibraryPage />} />
        <Route path="/accounts" element={<AccountsPage />} />
        <Route path="/sites/:siteId" element={<SiteSectionPage />} />
        <Route path="/sites/:siteId/:section" element={<SiteSectionPage />} />
      </Route>
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}
